import { useEffect, useState } from 'react'
import { trackError } from '@vend/utilities'
import { FEATURE_ENABLE_ACH_XSERIES } from '../../../../constants/wr-features'
import {
  fetchInvoicesBySaleId,
  getAchPayments,
  sumInTransitAchAmount,
} from '../../../../functions/invoicing/invoices-by-sale-id'
import { getFeatureFromCache } from '../../../../util/feature-cache'

const FETCH_ERROR = '[PaymentPanel] Failed to fetch invoices by sale tag'

export interface UseSaleInTransitAchResult {
  /** Flag on, sale has a server id, and not skipped (ABP or not on-account). */
  isActive: boolean
  /** This sale's processing ACH total in dollars. */
  inTransitAmount: number
  isLoading: boolean
  isError: boolean
}

const inactiveResult: UseSaleInTransitAchResult = {
  isActive: false,
  inTransitAmount: 0,
  isLoading: false,
  isError: false,
}

/**
 * Fetch this sale's invoices and sum processing ACH (bankAccount + processing).
 * Used to cap Continue Sale Pay on on-account sales. Does not use customer-level
 * pendingPaymentAmount. Callers skip ABP and non-on-account sales (parked cash,
 * ecom, layby) so an invoicing error cannot lock those Pay screens.
 */
export const useSaleInTransitAch = (
  saleId: string | undefined,
  options?: { skip?: boolean }
): UseSaleInTransitAchResult => {
  const isAchEnabled = getFeatureFromCache(FEATURE_ENABLE_ACH_XSERIES)
  const isActive = Boolean(isAchEnabled && saleId && !options?.skip)
  const requestKey = isActive && saleId ? saleId : ''

  const [readyKey, setReadyKey] = useState('')
  const [inTransitAmount, setInTransitAmount] = useState(0)
  const [isError, setIsError] = useState(false)
  const isLoading = isActive && readyKey !== requestKey

  useEffect(() => {
    if (!isActive || !saleId) {
      setReadyKey('')
      setInTransitAmount(0)
      setIsError(false)
      return
    }

    let isCurrent = true
    setIsError(false)

    fetchInvoicesBySaleId(saleId)
      .then(response => {
        if (!isCurrent) {
          return
        }
        setInTransitAmount(
          sumInTransitAchAmount(getAchPayments(response.invoices))
        )
        setReadyKey(saleId)
      })
      .catch(error => {
        trackError(FETCH_ERROR, error, { saleId })
        if (!isCurrent) {
          return
        }
        setInTransitAmount(0)
        setIsError(true)
        setReadyKey(saleId)
      })

    return () => {
      isCurrent = false
    }
  }, [isActive, saleId])

  if (!isActive) {
    return inactiveResult
  }

  return {
    isActive: true,
    inTransitAmount: isLoading ? 0 : inTransitAmount,
    isLoading,
    isError: isLoading ? false : isError,
  }
}
